import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import taxService from '../../services/taxService';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import './ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const TaxListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [taxes, setTaxes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTaxes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTaxes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await taxService.getTaxes(1, 100);
      if (response?.data) {
        setTaxes(response.data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('tax.failedToLoadTax'));
      setLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    navigate(`/products/taxes/${id}/delete`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await taxService.getTaxes(1, 100);
      if (response?.data) {
        setTaxes(response.data);
      }
    } catch (err) {
      setError(t('tax.failedToLoadTax'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'taxName',
      label: t('tax.taxName'),
      sortable: true,
      width: '220px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'taxCode',
      label: t('tax.code'),
      sortable: true,
      width: '120px',
      render: (value) => <span className="description-text">{value}</span>,
    },
    {
      key: 'taxPercentage',
      label: t('tax.taxRate'),
      sortable: true,
      width: '120px',
      render: (value) => `${value}%`,
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '120px',
      render: (value) => <span className="badge badge-active">{value ? t('common.active') : t('common.inactive')}</span>,
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/taxes/${item.taxId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Taxes.Edit) ? [{
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item: any) => navigate(`/products/taxes/${item.taxId}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    }] : []),
    ...(hasPermission(Permissions.Taxes.Delete) ? [{
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item: any) => handleDelete(item.taxId),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    }] : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('tax.title')}</h1>
          <div className="header-actions">
            {hasPermission(Permissions.Taxes.Create) && (
              <button
                className="btn-new"
                onClick={() => navigate('/products/taxes/create')}
                title={t('tax.newTax')}
              >
                <CirclePlus/>
                {t('tax.newTax')}
              </button>
            )}
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('tax.refreshTaxes')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('tax.refreshTaxes')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={taxes}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('tax.noTaxesFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default TaxListPage;
